/**
 * The buttons of a table column (TableCellConfig.buttons / an action column's
 * cellButtons): Approve / Reject / Request Info ... Each button has a label, a
 * colour and ONE feature - the same feature settings as a Button widget (the
 * embedded app-design-button-settings), popup screens included. The table runs
 * it with the clicked row as context.
 */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import type { TableCellButton, TableCellButtonTone } from '../../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { CellButtonFeatureComponent, type CellButtonOptions } from './cell-button-feature.component';

export { EMPTY_CELL_BUTTON_OPTIONS, type CellButtonOptions } from './cell-button-feature.component';

/** At most this many buttons in one cell. */
export const MAX_CELL_BUTTONS = 5;

const TONES: Array<{ label: string; value: TableCellButtonTone }> = [
  { label: 'Green', value: 'green' },
  { label: 'Red', value: 'red' },
  { label: 'Blue', value: 'blue' },
  { label: 'Amber', value: 'amber' },
  { label: 'Grey', value: 'grey' },
];

let buttonSeq = 0;
function newCellButton(index: number): TableCellButton {
  const tone = TONES[index % TONES.length].value;
  return { id: `cb_${Date.now().toString(36)}_${(buttonSeq++).toString(36)}`, label: `Button ${index + 1}`, tone, config: { action: 'none' } };
}

@Component({
  selector: 'app-design-cell-buttons',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, CellButtonFeatureComponent],
  template: `
<div class="cbs">
  <div class="cbs-head">
    <span>Buttons</span>
    <button type="button" class="cbs-add" [disabled]="buttons().length >= max" (click)="add()">
      <mat-icon>add</mat-icon> Add button
    </button>
  </div>
  <p class="cbs-hint" *ngIf="!buttons().length">
    Add buttons to show them in every cell of this column, e.g. Approve / Reject / Request Info.
    Each runs a feature like a Button widget - an API, a popup screen, navigation, approval, export ... - for the row clicked.
  </p>

  <div class="cbs-card" *ngFor="let button of buttons(); let i = index; let first = first; let last = last; trackBy: trackButton">
    <div class="cbs-row">
      <span class="cbs-preview" [attr.data-tone]="button.tone || 'blue'" [class.is-filled]="button.variant === 'filled'">{{ button.label || '…' }}</span>
      <span class="cbs-spacer"></span>
      <button type="button" class="cbs-icon" [disabled]="first" (click)="move(i, -1)" matTooltip="Move left" aria-label="Move left"><mat-icon>arrow_back</mat-icon></button>
      <button type="button" class="cbs-icon" [disabled]="last" (click)="move(i, 1)" matTooltip="Move right" aria-label="Move right"><mat-icon>arrow_forward</mat-icon></button>
      <button type="button" class="cbs-icon" (click)="toggleOpen(button.id)" [attr.aria-expanded]="isOpen(button.id)" matTooltip="Settings" aria-label="Button settings">
        <mat-icon>{{ isOpen(button.id) ? 'expand_less' : 'tune' }}</mat-icon>
      </button>
      <button type="button" class="cbs-icon is-danger" (click)="remove(i)" matTooltip="Remove" aria-label="Remove button"><mat-icon>close</mat-icon></button>
    </div>

    <div class="cbs-body" *ngIf="isOpen(button.id)">
      <label class="cbs-field">
        <span>Label</span>
        <input type="text" [ngModel]="button.label" (ngModelChange)="patch(i, { label: $event })" placeholder="Approve" />
      </label>
      <div class="cbs-pair">
        <label class="cbs-field">
          <span>Colour</span>
          <select [ngModel]="button.tone || 'blue'" (ngModelChange)="patch(i, { tone: $event })">
            <option *ngFor="let tone of tones" [value]="tone.value">{{ tone.label }}</option>
          </select>
        </label>
        <label class="cbs-field">
          <span>Style</span>
          <select [ngModel]="button.variant || 'outline'" (ngModelChange)="patch(i, { variant: $event })">
            <option value="outline">Outline</option>
            <option value="filled">Filled</option>
          </select>
        </label>
      </div>

      <app-design-cell-button-feature
        [config]="button.config"
        [label]="button.label"
        [tableName]="tableName()"
        [context]="context()"
        (configChange)="patchConfig(i, $event)"
      ></app-design-cell-button-feature>
    </div>
  </div>
</div>
`,
  styles: [`
    :host { display: block; font-size: 12px; color: #344054; }
    .cbs { margin-top: 8px; border-top: 1px dashed #eaecf0; padding-top: 8px; }
    .cbs-head { display: flex; align-items: center; justify-content: space-between; font-weight: 600; color: #101828; }
    .cbs-add { display: inline-flex; align-items: center; gap: 2px; padding: 2px 8px; border: 1px solid #d0d5dd; border-radius: 6px; background: #fff; color: #2970ff; font: inherit; cursor: pointer; }
    .cbs-add:disabled { color: #98a2b3; cursor: default; }
    .cbs-add mat-icon { width: 16px; height: 16px; font-size: 16px; }
    .cbs-hint { margin: 6px 0 0; color: #667085; line-height: 1.45; }
    .cbs-card { margin-top: 6px; border: 1px solid #eaecf0; border-radius: 6px; background: #fff; }
    .cbs-row { display: flex; align-items: center; gap: 2px; padding: 4px 4px 4px 8px; }
    .cbs-spacer { flex: 1 1 auto; }
    .cbs-icon { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; padding: 0; border: 0; border-radius: 4px; background: none; color: #667085; cursor: pointer; }
    .cbs-icon:disabled { opacity: .35; cursor: default; }
    .cbs-icon:not(:disabled):hover { background: #f2f4f7; color: #2970ff; }
    .cbs-icon.is-danger:hover { color: #d92d20; }
    .cbs-icon mat-icon { width: 16px; height: 16px; font-size: 16px; }
    .cbs-body { padding: 6px 8px 8px; border-top: 1px solid #f2f4f7; }
    .cbs-field { display: flex; flex-direction: column; gap: 3px; margin-bottom: 6px; flex: 1 1 0; min-width: 0; }
    .cbs-field > span { color: #475467; }
    .cbs-field input, .cbs-field select { width: 100%; box-sizing: border-box; padding: 4px 6px; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit; background: #fff; }
    .cbs-pair { display: flex; gap: 8px; }
    .cbs-preview { --tone: #2563eb; --soft: #eff6ff; padding: 1px 8px; border: 1px solid var(--tone); border-radius: 4px; background: var(--soft); color: var(--tone); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60%; }
    .cbs-preview[data-tone='green'] { --tone: #16a34a; --soft: #f0fdf4; }
    .cbs-preview[data-tone='red'] { --tone: #dc2626; --soft: #fef2f2; }
    .cbs-preview[data-tone='amber'] { --tone: #d97706; --soft: #fffbeb; }
    .cbs-preview[data-tone='grey'] { --tone: #475467; --soft: #f2f4f7; }
    .cbs-preview.is-filled { background: var(--tone); color: #fff; }
  `],
})
export class CellButtonsComponent {
  readonly buttons = input<TableCellButton[]>([]);
  /** The option lists (the inspector passes them); absent = worked out from the design here. */
  readonly context = input<CellButtonOptions | null>(null);
  /** The table's name - popup screens are named after it. */
  readonly tableName = input('');
  readonly buttonsChange = output<TableCellButton[]>();

  readonly tones = TONES;
  readonly max = MAX_CELL_BUTTONS;
  private readonly openIds = signal<ReadonlySet<string>>(new Set());

  isOpen(id: string): boolean { return this.openIds().has(id); }
  toggleOpen(id: string): void {
    this.openIds.update((open) => {
      const next = new Set(open);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }
  trackButton = (_: number, button: TableCellButton) => button.id;

  add(): void {
    if (this.buttons().length >= this.max) return;
    const button = newCellButton(this.buttons().length);
    this.emit([...this.buttons(), button]);
    this.toggleOpen(button.id);
  }

  remove(index: number): void {
    this.emit(this.buttons().filter((_, i) => i !== index));
  }

  move(index: number, step: -1 | 1): void {
    const next = [...this.buttons()];
    const target = index + step;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    this.emit(next);
  }

  patch(index: number, patch: Partial<TableCellButton>): void {
    this.emit(this.buttons().map((button, i) => (i === index ? { ...button, ...patch } : button)));
  }

  /** The embedded Button settings changed a feature key. */
  patchConfig(index: number, patch: Record<string, unknown>): void {
    this.emit(this.buttons().map((button, i) => (i === index ? { ...button, config: { ...button.config, ...patch } } : button)));
  }

  private emit(buttons: TableCellButton[]): void {
    this.buttonsChange.emit(buttons);
  }
}
